/**
 * Audio: one AudioContext resumed on the login click (autoplay policy), tones (ringtone,
 * call-waiting beep, AU ringback) generated with oscillators, routed through a
 * MediaStreamAudioDestinationNode into <audio> elements so `setSinkId` can target the headset
 * (ringer) and optionally the speaker. Remote call audio plays in a per-line <audio> element on
 * the speaker sink. `state` flips to 'suspended' when activation is lost -> the UI shows the
 * red "Click to enable sound" strip.
 */
import { signal } from '@preact/signals';
import { AU_RINGBACK_HZ } from '../sip/ringback.js';
import { applySink, type DevicePrefs } from './devices.js';

export type AudioState = 'idle' | 'suspended' | 'running' | 'unsupported';

export interface AudioEngineDeps {
  createContext?: () => AudioContext;
  createAudio?: () => HTMLAudioElement;
}

interface Tone {
  oscillators: OscillatorNode[];
  gain: GainNode;
}

export class AudioEngine {
  readonly state = signal<AudioState>('idle');
  private ctx: AudioContext | null = null;
  private dest: MediaStreamAudioDestinationNode | null = null;
  private ringerEl: HTMLAudioElement | null = null;
  private speakerRingEl: HTMLAudioElement | null = null;
  private readonly remote = new Map<string, HTMLAudioElement>();
  private ringTone: Tone | null = null;
  private ringPattern: ReturnType<typeof setInterval> | null = null;
  private ringbackTone: Tone | null = null;
  private prefs: DevicePrefs = { mic: null, speaker: null, ringer: null, ringAlsoSpeaker: false };
  private readonly createContext: () => AudioContext;
  private readonly createAudio: () => HTMLAudioElement;

  constructor(d: AudioEngineDeps = {}) {
    this.createContext = d.createContext ?? (() => new AudioContext());
    this.createAudio = d.createAudio ?? (() => new Audio());
  }

  /** must run inside a user gesture (the login click) */
  async unlock(): Promise<boolean> {
    try {
      if (!this.ctx) {
        this.ctx = this.createContext();
        this.dest = this.ctx.createMediaStreamDestination();
        this.ringerEl = this.createAudio();
        this.ringerEl.srcObject = this.dest.stream;
        this.ringerEl.autoplay = true;
        this.ctx.addEventListener('statechange', () => this.syncState());
      }
      if (this.ctx.state !== 'running') await this.ctx.resume();
      await this.ringerEl?.play().catch(() => undefined);
      await this.setSinks(this.prefs);
      this.syncState();
      return this.ctx.state === 'running';
    } catch {
      this.state.value = 'unsupported';
      return false;
    }
  }

  private syncState(): void {
    if (!this.ctx) return;
    this.state.value = this.ctx.state === 'running' ? 'running' : 'suspended';
  }

  get unlocked(): boolean {
    return this.state.value === 'running';
  }

  async setSinks(prefs: DevicePrefs): Promise<void> {
    this.prefs = prefs;
    if (this.ringerEl) await applySink(this.ringerEl, prefs.ringer);
    if (prefs.ringAlsoSpeaker && this.dest) {
      if (!this.speakerRingEl) {
        this.speakerRingEl = this.createAudio();
        this.speakerRingEl.srcObject = this.dest.stream;
        this.speakerRingEl.autoplay = true;
        await this.speakerRingEl.play().catch(() => undefined);
      }
      await applySink(this.speakerRingEl, prefs.speaker);
    } else if (this.speakerRingEl) {
      this.speakerRingEl.pause();
      this.speakerRingEl.srcObject = null;
      this.speakerRingEl = null;
    }
    for (const el of this.remote.values()) await applySink(el, prefs.speaker);
  }

  private tone(freqs: readonly number[], gainValue: number): Tone | null {
    if (!this.ctx || !this.dest) return null;
    const gain = this.ctx.createGain();
    gain.gain.value = gainValue;
    gain.connect(this.dest);
    const oscillators = freqs.map((f) => {
      const o = this.ctx!.createOscillator();
      o.type = 'sine';
      o.frequency.value = f;
      o.connect(gain);
      o.start();
      return o;
    });
    return { oscillators, gain };
  }

  private stopTone(t: Tone | null): void {
    if (!t) return;
    for (const o of t.oscillators) {
      try {
        o.stop();
        o.disconnect();
      } catch {
        /* already stopped */
      }
    }
    t.gain.disconnect();
  }

  /** ringtone: 440 + 480 Hz, 1 s on / 2 s off */
  ringing(on: boolean): void {
    if (!on) {
      if (this.ringPattern) clearInterval(this.ringPattern);
      this.ringPattern = null;
      this.stopTone(this.ringTone);
      this.ringTone = null;
      return;
    }
    if (this.ringTone || !this.ctx) return;
    this.ringTone = this.tone([440, 480], 0.15);
    let phase = 0;
    const step = (): void => {
      if (!this.ringTone) return;
      this.ringTone.gain.gain.value = phase % 3 === 0 ? 0.15 : 0;
      phase += 1;
    };
    step();
    this.ringPattern = setInterval(step, 1000);
  }

  /** short 440 Hz beep for call waiting on the earpiece */
  callWaitingBeep(): void {
    const t = this.tone([440], 0.1);
    if (!t) return;
    setTimeout(() => this.stopTone(t), 300);
  }

  /** AU ringback tone pair; the Ringback cadence drives on/off */
  setRingbackTone(on: boolean): void {
    if (on) {
      if (!this.ringbackTone) this.ringbackTone = this.tone(AU_RINGBACK_HZ, 0.12);
    } else {
      this.stopTone(this.ringbackTone);
      this.ringbackTone = null;
    }
  }

  attachRemote(lineId: string, stream: MediaStream): void {
    let el = this.remote.get(lineId);
    if (!el) {
      el = this.createAudio();
      el.autoplay = true;
      this.remote.set(lineId, el);
    }
    el.srcObject = stream;
    void applySink(el, this.prefs.speaker);
    el.play().catch(() => undefined);
  }

  detachRemote(lineId: string): void {
    const el = this.remote.get(lineId);
    if (!el) return;
    el.pause();
    el.srcObject = null;
    this.remote.delete(lineId);
  }

  setRemoteMuted(lineId: string, muted: boolean): void {
    const el = this.remote.get(lineId);
    if (el) el.muted = muted;
  }

  /** a 1 s test tone on the speaker sink (self-check) */
  async testSpeaker(): Promise<boolean> {
    if (!this.ctx || !this.dest) return false;
    const el = this.createAudio();
    el.srcObject = this.dest.stream;
    await applySink(el, this.prefs.speaker);
    await el.play().catch(() => undefined);
    const t = this.tone([660], 0.15);
    await new Promise((r) => setTimeout(r, 1000));
    this.stopTone(t);
    el.pause();
    el.srcObject = null;
    return true;
  }

  dispose(): void {
    this.ringing(false);
    this.setRingbackTone(false);
    for (const id of [...this.remote.keys()]) this.detachRemote(id);
    this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    this.state.value = 'idle';
  }
}
